import { RoadmapNode, RoadmapNodeStatus } from '@/api/rest/resources/types/organization';
import { Workstream, WORKSTREAM_MILESTONE_STATUS } from '@/api/rest/resources/types/workstreams';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { isNullish } from '@/utils/booleans';

export const getRoadmapFromWorkstream = (workstream: Workstream): RoadmapNode | null => {
  const roadmaps = workstream.roadmap_nodes;

  if (!Array.isArray(roadmaps) || roadmaps.length === 0) {
    return null;
  }

  if (roadmaps.length > 1) {
    throw new Error(`Workstream ${workstream.id} has more than one roadmap node, which is not supported yet`);
  }

  return roadmaps[0] as RoadmapNode;
};

export const getMilestoneCompleteStatus = (
  milestone: RoadmapNode,
): {
  completed: number;
  total: number;
} => {
  if (!milestone.children || milestone.children.length === 0) {
    return { completed: 0, total: 0 };
  }

  const visibleMilestoneChildren = milestone.children.filter((child) => child.visible_to_customer);

  const total = visibleMilestoneChildren.length;
  const completed = visibleMilestoneChildren.filter((child) => child.status === RoadmapNodeStatus.closed).length;

  return { completed, total };
};

export const getWorkstreamMilestones = (
  workstream: Workstream,
): {
  completed: number;
  total: number;
} => {
  const roadmap = getRoadmapFromWorkstream(workstream);

  if (!roadmap || !roadmap.children || roadmap.children.length === 0) {
    return { completed: 0, total: 0 };
  }

  return getMilestoneCompleteStatus(roadmap);
};

export const getWorkstreamProgress = (workstream: Workstream): number => {
  if (!isNullish(workstream.progress)) {
    return parseFloat(getDisplayNumber(workstream.progress));
  }

  const { completed, total } = getWorkstreamMilestones(workstream);

  return total !== 0 ? parseFloat(getDisplayNumber((completed / total) * 100)) : 0;
};

export const getWorkstreamMilestoneStatus = (
  status: RoadmapNodeStatus,
): (typeof WORKSTREAM_MILESTONE_STATUS)[keyof typeof WORKSTREAM_MILESTONE_STATUS] => {
  if (status === RoadmapNodeStatus.closed) {
    return WORKSTREAM_MILESTONE_STATUS.COMPLETE;
  }

  if (status === RoadmapNodeStatus.active) {
    return WORKSTREAM_MILESTONE_STATUS.IN_PROGRESS;
  }

  if ([RoadmapNodeStatus.ready, RoadmapNodeStatus.error, RoadmapNodeStatus.created].includes(status)) {
    return WORKSTREAM_MILESTONE_STATUS.LOCKED;
  }

  return WORKSTREAM_MILESTONE_STATUS.UNKNOWN;
};

export const getMilestoneProgress = (milestone: RoadmapNode): number => {
  const milestoneStatus = getWorkstreamMilestoneStatus(milestone.status);

  const isCompleted = milestoneStatus === WORKSTREAM_MILESTONE_STATUS.COMPLETE;

  if (isCompleted) {
    return 100;
  }

  const isNotStarted =
    milestoneStatus === WORKSTREAM_MILESTONE_STATUS.UNKNOWN || milestoneStatus === WORKSTREAM_MILESTONE_STATUS.LOCKED;
  const children = milestone.children ?? [];

  if (isNotStarted || children.length === 0) {
    return 0;
  }

  const { total, completed } = getMilestoneCompleteStatus(milestone);
  const percentage = (completed / total) * 100;
  return parseInt(getDisplayNumber(percentage), 10); // We don't need decimal precision for milestone progress
};
